import { auth } from '@/firebase-config';
import { onAuthStateChanged, User, Auth, signOut } from 'firebase/auth';
import { defineStore } from 'pinia';
import { Ref, ref } from 'vue';

/**
 * Pinia store for managing user authentication state.
 *
 * This store:
 * - Tracks the currently authenticated Firebase user
 * - Maintains admin and server admin permission flags
 * - Automatically updates when Firebase authentication state changes
 * - Resets admin flags when user signs out
 *
 * @returns {Object} Store instance with reactive user state
 * @returns {Ref<User | null>} .user - The currently authenticated Firebase user or null
 * @returns {Ref<boolean>} .admin - Whether the user has admin or acumapify-admin privileges
 * @returns {Ref<boolean>} .serverAdmin - Whether the user has server-level admin privileges
 *
 * @example
 * ```ts
 * const userStore = useUserStore();
 *
 * // Check if user is logged in
 * if (userStore.user) {
 *   console.log('User email:', userStore.user.email);
 * }
 *
 * // Check admin status
 * if (userStore.admin) {
 *   // Show admin features
 * }
 * ```
 */
export const useUserStore = defineStore('user', () => {
  const user: Ref<User | null> = ref(null);
  const admin: Ref<boolean> = ref(false);
  const serverAdmin: Ref<boolean> = ref(false);

  onAuthStateChanged(auth, (u) => {
    if (u) {
      user.value = u;
    } else {
      user.value = null;
      admin.value = false;
      serverAdmin.value = false;
    }
  });

  return { user, admin, serverAdmin };
});

/**
 * Confirms that the currently authenticated user has access to Acumapify.
 *
 * This function:
 * - Retrieves the Firebase ID token claims for the current user
 * - Verifies that the user has at least one allowed claim
 *   (`admin`, `acumapify_admin`, or `acumapify_basic`)
 * - Signs the user out and throws if no valid access is found
 * - Updates the user store with whether the user has admin-level access
 *
 * @throws `"unauthorized-email"` - if the user lacks required claims
 */
export async function confirmAccess(auth: Auth): Promise<void> {
  if (!auth.currentUser) return; // return if no user is logged in
  const claims = (await auth.currentUser.getIdTokenResult()).claims; // extract claims
  const allowed = claims.admin || claims['acumapify-admin'] || claims['acumapify-basic']; // check for access to acumapify
  if (!allowed) {
    await signOut(auth); // sign out the user
    throw new Error('unauthorized-email'); // throw error if no access found
  }
  const adminClaim = !!(claims.admin || claims['acumapify-admin']); // check for admin claim
  const serverAdminClaim = !!claims.admin; // check for server admin claim
  const userStore = useUserStore(); // access the store
  userStore.$patch({ admin: adminClaim, serverAdmin: serverAdminClaim }); // update user store with user and admin claim if found
}

/**
 * Gets the authenticated user's id and ID token from the user store.
 *
 * @throws `"401"` when no user is logged in.
 */
export async function getAuthContext(): Promise<{ userId: string; idToken: string }> {
  const userStore = useUserStore();
  if (!userStore.user) {
    throw new Error('401');
  }
  const userId = userStore.user.uid;
  const idToken = await userStore.user.getIdToken();
  return { userId, idToken };
}
